import { useCallback, useEffect, useRef, useState } from 'react';

import { AuthError, isPending, type AuthApi, type PurchaseOrder,
         type Session } from '../auth/api';
import { addPending, dropPending, listPending } from './pending';

/** 轮询间隔。PIX 到账通常几秒；这是**后台**的那条，可以比面板上慢一点。 */
const POLL_MS = 5000;

/**
 * 盯着所有待付订单，到终态就报一次。
 *
 * **挂在 App 层**，不是挂在收款面板里 —— 用户关掉面板、切到别的标签页、
 * 甚至进了游戏，那笔到账都得告诉他。不然他只会看到余额莫名其妙变了，
 * 或者更糟：以为没付成功，又付一次。
 *
 * 页面被切到后台时**不轮询**：手机上那是常态（用户正在银行 App 里），
 * 一直轮只是白耗电。回到前台立刻补一次。
 */
export function usePaymentWatcher(
  api: AuthApi, session: Session | null,
  onSettled: (o: PurchaseOrder) => void,
) {
  const [pending, setPending] = useState<string[]>(() => listPending());
  // onSettled 每次渲染都是新函数，放进依赖会让定时器不停重建
  const cb = useRef(onSettled);
  useEffect(() => { cb.current = onSettled; }, [onSettled]);

  const watch = useCallback((orderNo: string) => {
    addPending(orderNo);
    setPending(listPending());
  }, []);

  const forget = useCallback((orderNo: string) => {
    dropPending(orderNo);
    setPending(listPending());
  }, []);

  const tick = useCallback(async () => {
    if (!session || document.hidden) return;
    const list = listPending();
    if (!list.length) return;
    for (const no of list) {
      try {
        const o = await api.order(session.token, no);
        if (isPending(o.status)) continue;
        // 到终态了：**先从待办里去掉再报**，不然报的那一下如果抛了，
        // 下一轮会再报一次。
        dropPending(no);
        setPending(listPending());
        cb.current(o);
      } catch (e) {
        // **要分清"这单不在了"和"网络抖了一下"。**
        //
        //   4xx（不含 429）= 服务端明确说了它不存在、或者不属于这个账号。
        //                    换过账号之后上一个账号留下的单号就是这样 ——
        //                    留着的话它会被永远查下去，每轮一次 409。
        //   其余（网络、5xx、429）= 下一轮再说。**不能丢** ——
        //                    丢了这笔单就再也没人盯了。
        const st = e instanceof AuthError ? e.status : 0;
        if (st >= 400 && st < 500 && st !== 429) {
          dropPending(no);
          setPending(listPending());
        }
      }
    }
  }, [api, session]);

  useEffect(() => {
    if (!session || !pending.length) return;
    const id = setInterval(() => void tick(), POLL_MS);
    const onVisible = () => { if (!document.hidden) void tick(); };
    document.addEventListener('visibilitychange', onVisible);
    void tick();            // 挂上就先查一次（刚回到前台的那种情况）
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [session, pending.length, tick]);

  return { pending, watch, forget };
}
